import React from 'react';
import { Loader2, HardDrive, FileText, FileAudio, TrendingDown, Copy } from 'lucide-react';

const formatMB = (mb) => {
  if (mb === undefined || mb === null) return '0';
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  return `${(mb * 1024).toFixed(0)} KB`;
};

export const StorageTab = ({ data, loading }) => {
  if (loading || !data) {
    return (
      <div className="mt-6  border border-[var(--isp-glass-border)] bg-slate-900/60 backdrop-blur-xl p-8">
        <div className="flex items-center gap-3 text-[var(--isp-body)]">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading storage analytics...
        </div>
      </div>
    );
  }

  const { documents, audio, totals } = data;
  const hasData = totals.actual_storage_mb > 0 || documents.total_files > 0 || audio.total_recordings > 0;

  return (
    <div className="mt-6 space-y-6" data-testid="admin-storage-tab">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className=" border border-[var(--isp-glass-border)] bg-slate-900/60 backdrop-blur-xl p-5" data-testid="storage-actual">
          <div className="flex items-center gap-2 text-[var(--isp-body)]">
            <HardDrive className="w-4 h-4" />
            <p className="text-xs">Actual Storage Used</p>
          </div>
          <p className="mt-2 text-2xl font-bold text-white">{formatMB(totals.actual_storage_mb)}</p>
          <p className="text-xs text-[var(--isp-muted)] mt-1">Without optimization: {formatMB(totals.without_optimization_mb)}</p>
        </div>
        <div className=" border border-emerald-500/20 bg-emerald-500/5 backdrop-blur-xl p-5" data-testid="storage-saved">
          <div className="flex items-center gap-2 text-emerald-400">
            <TrendingDown className="w-4 h-4" />
            <p className="text-xs">Total Saved</p>
          </div>
          <p className="mt-2 text-2xl font-bold text-emerald-400">{formatMB(totals.total_saved_mb)}</p>
          <p className="text-xs text-emerald-400/70 mt-1">{totals.cost_reduction_pct}% cost reduction</p>
        </div>
        <div className=" border border-[var(--isp-glass-border)] bg-slate-900/60 backdrop-blur-xl p-5" data-testid="storage-docs">
          <div className="flex items-center gap-2 text-[var(--isp-body)]">
            <FileText className="w-4 h-4" />
            <p className="text-xs">Documents</p>
          </div>
          <p className="mt-2 text-2xl font-bold text-white">{documents.total_files}</p>
          <p className="text-xs text-[var(--isp-muted)] mt-1">{formatMB(documents.total_size_mb)} total</p>
        </div>
        <div className=" border border-[var(--isp-glass-border)] bg-slate-900/60 backdrop-blur-xl p-5" data-testid="storage-audio">
          <div className="flex items-center gap-2 text-[var(--isp-body)]">
            <FileAudio className="w-4 h-4" />
            <p className="text-xs">Audio Recordings</p>
          </div>
          <p className="mt-2 text-2xl font-bold text-white">{audio.total_recordings}</p>
          <p className="text-xs text-[var(--isp-muted)] mt-1">{formatMB(audio.compressed_size_mb)} stored</p>
        </div>
      </div>

      <div className=" border border-[var(--isp-glass-border)] bg-slate-900/60 backdrop-blur-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--isp-glass-border)]">
          <h2 className="text-[var(--isp-heading)] font-semibold flex items-center gap-2">
            <FileAudio className="w-4 h-4 text-cyan-400" />
            Audio Compression
          </h2>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-slate-950/40 rounded-xl p-4 border border-[var(--isp-glass-border)]">
              <p className="text-xs text-[var(--isp-body)]">Original Size</p>
              <p className="text-lg font-bold text-white mt-1">{formatMB(audio.original_size_mb)}</p>
            </div>
            <div className="bg-slate-950/40 rounded-xl p-4 border border-[var(--isp-glass-border)]">
              <p className="text-xs text-[var(--isp-body)]">After Compression</p>
              <p className="text-lg font-bold text-cyan-400 mt-1">{formatMB(audio.compressed_size_mb)}</p>
            </div>
            <div className="bg-emerald-500/5 rounded-xl p-4 border border-emerald-500/20">
              <p className="text-xs text-emerald-400">Compression Saved</p>
              <p className="text-lg font-bold text-emerald-400 mt-1">{formatMB(audio.compression_saved_mb)}</p>
            </div>
          </div>
          {audio.total_recordings > 0 && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-[var(--isp-body)] mb-1">
                <span>Compression ratio</span>
                <span className="text-emerald-400 font-semibold">
                  {audio.original_size_mb > 0 ? Math.round((1 - audio.compressed_size_mb / audio.original_size_mb) * 100) : 0}% smaller
                </span>
              </div>
              <div className="w-full bg-slate-950/60 rounded-full h-3 border border-[var(--isp-glass-border)]">
                <div
                  className="bg-gradient-to-r from-cyan-500 to-emerald-500 h-full rounded-full transition-all"
                  style={{ width: `${audio.original_size_mb > 0 ? Math.max(5, (audio.compressed_size_mb / audio.original_size_mb) * 100) : 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className=" border border-[var(--isp-glass-border)] bg-slate-900/60 backdrop-blur-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--isp-glass-border)]">
          <h2 className="text-[var(--isp-heading)] font-semibold flex items-center gap-2">
            <Copy className="w-4 h-4 text-amber-400" />
            Duplicate Detection
          </h2>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-slate-950/40 rounded-xl p-4 border border-[var(--isp-glass-border)]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[var(--isp-body)]">Document Duplicates Caught</p>
                  <p className="text-2xl font-bold text-white mt-1">{documents.duplicates_caught}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[var(--isp-body)]">Storage Saved</p>
                  <p className="text-lg font-bold text-emerald-400 mt-1">{formatMB(documents.dedup_saved_mb)}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-950/40 rounded-xl p-4 border border-[var(--isp-glass-border)]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[var(--isp-body)]">Audio Duplicates Caught</p>
                  <p className="text-2xl font-bold text-white mt-1">{audio.duplicates_caught}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[var(--isp-body)]">Reused</p>
                  <p className="text-lg font-bold text-emerald-400 mt-1">
                    {audio.duplicates_caught > 0 ? `${audio.duplicates_caught} file${audio.duplicates_caught > 1 ? 's' : ''}` : 'None yet'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {hasData && (
        <div className=" border border-purple-500/20 bg-purple-500/5 backdrop-blur-xl p-5">
          <h3 className="text-sm font-semibold text-purple-300 flex items-center gap-2">
            <TrendingDown className="w-4 h-4" />
            Cost Impact
          </h3>
          <p className="text-xs text-[var(--isp-body)] mt-2">
            Effective storage: <span className="text-emerald-400 font-semibold">{formatMB(totals.actual_storage_mb)}</span> instead of <span className="text-red-400">{formatMB(totals.without_optimization_mb)}</span>.
            {totals.cost_reduction_pct > 0 && <> That's a <span className="text-emerald-400 font-bold">{totals.cost_reduction_pct}%</span> reduction.</>}
          </p>
        </div>
      )}
    </div>
  );
};
